import { useOpenAccountSettlement, useOpenTabs } from '@/features/openTabs/contexts/OpenTabsData'
import {
    getAccountAmountDue,
    getAccountItemRemainder,
    getAccountItemTotal,
    getAccountPaymentItemTotals,
    getAccountPaymentTotal,
    isValidAccountSelection,
} from '@/features/openTabs/utils/accountCalculations'
import { formatAccountPrice, toSplitItemView } from '@/features/openTabs/utils/viewModels'
import currencyJs from 'currency.js'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'

const useSplitAccountPage = () => {
    const router = useRouter()
    const { t } = useTranslation('app')
    const { selectedAccount, detailLoading, detailFailed, detailRefreshFailed, listFailed } =
        useOpenTabs()
    const {
        settlingAccount,
        paymentSelection,
        remainingPaymentItems,
        selectedPaymentItems,
        selectedPaymentTotal,
        moveItemToPayment,
        moveItemFromPayment,
        clearPaymentSelection,
    } = useOpenAccountSettlement()
    const splitItemPaymentTotals = getAccountPaymentItemTotals(selectedAccount, paymentSelection)
    const currency = selectedAccount?.currency ?? ''
    const selectionDisabled =
        detailLoading || detailFailed || detailRefreshFailed || listFailed || settlingAccount
    const canContinue =
        !selectionDisabled && isValidAccountSelection(selectedAccount, paymentSelection)

    const handleCancel = () => {
        clearPaymentSelection()
        router.dismissTo('/open-tabs')
    }

    const handleContinue = () => {
        if (!canContinue) return

        router.push({ pathname: '/open-tabs/payment', params: { mode: 'split' } })
    }

    return {
        selectedAccount,
        remainingItems: remainingPaymentItems.map((splitItem) => {
            const remainder = getAccountItemRemainder(
                splitItem.item,
                paymentSelection[splitItem.item.id] ?? 0
            )
            return toSplitItemView(splitItem, t, getAccountItemTotal(remainder))
        }),
        selectedItems: selectedPaymentItems.map((item) =>
            toSplitItemView(item, t, splitItemPaymentTotals[item.item.id])
        ),
        paymentTotalPrice: formatAccountPrice(
            getAccountPaymentTotal(selectedAccount, paymentSelection),
            currency
        ),
        remainingTotalPrice: formatAccountPrice(
            Math.max(
                currencyJs(selectedAccount ? getAccountAmountDue(selectedAccount) : 0).subtract(
                    selectedPaymentTotal
                ).value,
                0
            ),
            currency
        ),
        canContinue,
        selectionDisabled,
        handleCancel,
        handleContinue,
        handleMoveToSplit: moveItemToPayment,
        handleMoveFromSplit: moveItemFromPayment,
    }
}

export default useSplitAccountPage
